import React, { useCallback, useEffect } from 'react';

import includes from 'lodash/includes';

import { Grid, Section, Alert } from '@gogame/components';
import { FormField, withForm, SidePageV2 } from '@gogame/form';

import {
  loadApplications,
  loadNetworks,
  loadPlatforms,
  loadBriefs,
  loadPromoCodes,
  loadLandingPages,
  loadCustomModels,
} from 'scenes/Influencers/MasterCampaigns/sources/fields';

import Agencies from './Agencies';
import {
  NAME,
  GENERATE_NAME_AUTOMATICALLY,
  PERIOD,
  APPLICATION,
  NETWORK,
  PLATFORMS,
  BRIEFS,
  PROMO_CODE,
  LANDING_PAGE,
  DESCRIPTION,
  TOTAL_BUDGET,
  COST_MODEL,
  PAYOUT_RATIOS,
  ANDROID,
  IOS,
  PLARIUM_PLAY,
  PORTAL,
  DEPENDENCIES,
  ATTRIBUTION_PROVIDER_ID,
} from './constants';
import { MasterCampaignFormValidationSchema } from './schemas';
import useValidation from './useValidation';

const MasterCampaignForm = withForm({
  validationSchema: MasterCampaignFormValidationSchema,
  enableReinitialize: true,
})(({
  touched,
  children,
  title,
  subTitle,
  values,
  initialValues,
  setFieldValue,
  loading,
  onClose,
  onExited,
  open,
  entityId,
}) => {
  const {
    [GENERATE_NAME_AUTOMATICALLY]: generateNameAutomatically,
    [PLATFORMS]: platforms,
  } = values;

  const selectedAndroidPlatform = includes(platforms, ANDROID);
  const selectedIOSPlatform = includes(platforms, IOS);
  const selectedPlariumPlayPlatform = includes(platforms, PLARIUM_PLAY);
  const selectedPortalPlatform = includes(platforms, PORTAL);

  const {
    validationName,
    validationPayoutAndroid,
    validationPayoutIOS,
    validationPayoutPlariumPlay,
    validationPayoutPortal,
    validationLandingPage,
  } = useValidation({
    generateNameAutomatically,
    selectedAndroidPlatform,
    selectedIOSPlatform,
    selectedPlariumPlayPlatform,
    selectedPortalPlatform,
  });

  useEffect(() => {
    if (!touched[PLATFORMS]) {
      return;
    }

    const selected = [
      { name: ANDROID, value: selectedAndroidPlatform },
      { name: IOS, value: selectedIOSPlatform },
      { name: PLARIUM_PLAY, value: selectedPlariumPlayPlatform },
      { name: PORTAL, value: selectedPortalPlatform },
    ].filter(({ value }) => value);

    if (selected.length === 1) {
      setFieldValue(PAYOUT_RATIOS, {
        [selected[0].name]: 100,
      });
      return;
    }

    if (selected.length === 2) {
      setFieldValue(PAYOUT_RATIOS, {
        [selected[0].name]: 50,
        [selected[1].name]: 50,
      });
      return;
    }

    const defaultValues = selected.some((v) => v.name === PLARIUM_PLAY)
      ? {
          [ANDROID]: 37.5,
          [IOS]: 37.5,
          [PLARIUM_PLAY]: 25,
        }
      : {
          [ANDROID]: 37.5,
          [IOS]: 37.5,
          [PORTAL]: 25,
        };

    setFieldValue(PAYOUT_RATIOS, defaultValues);
  }, [
    selectedIOSPlatform,
    selectedAndroidPlatform,
    selectedPlariumPlayPlatform,
    selectedPortalPlatform,
  ]);

  const handleIsCostModelOptionDisabled = useCallback(
    (option, selectedOptions) => {
      const selectedOptionsIds = selectedOptions.map(({ id }) => id);

      if (selectedOptionsIds?.length) {
        return (
          (option.id === PLARIUM_PLAY && selectedOptionsIds.includes(PORTAL)) ||
          (option.id === PORTAL && selectedOptionsIds.includes(PLARIUM_PLAY))
        );
      } else {
        return false;
      }
    },
    []
  );

  const hasConversionPage =
    selectedPlariumPlayPlatform || selectedPortalPlatform;

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Section title={'General'}>
          <Grid container spacing={3}>
            <Grid item xs={8}>
              <FormField.TextField
                name={NAME}
                label={'Name'}
                padding={0}
                disabled={generateNameAutomatically}
                validate={validationName}
                required={!values[GENERATE_NAME_AUTOMATICALLY]}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Switch
                padding={0}
                name={GENERATE_NAME_AUTOMATICALLY}
                label={'Generate name automatically'}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.MonthPicker
                name={PERIOD}
                label={'Period'}
                padding={0}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Application'}
                padding={0}
                name={APPLICATION}
                source={loadApplications}
                lazy={true}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Network'}
                padding={0}
                name={NETWORK}
                depends={DEPENDENCIES[NETWORK]}
                source={loadNetworks}
                lazy={true}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.MultiSelect
                label={'Platforms'}
                padding={0}
                name={PLATFORMS}
                source={loadPlatforms}
                lazy={true}
                required={true}
                withSelectAll={false}
                isOptionDisabled={handleIsCostModelOptionDisabled}
              />
            </Grid>
            <Grid item xs={hasConversionPage ? 4 : 8}>
              <FormField.MultiSelect
                name={BRIEFS}
                label={'Briefs'}
                padding={0}
                depends={DEPENDENCIES[BRIEFS]}
                source={loadBriefs}
                required={true}
                lazy={true}
              />
            </Grid>
            {hasConversionPage ? (
              <Grid item xs={4}>
                <FormField.Select
                  name={LANDING_PAGE}
                  label={'Conversion page'}
                  padding={0}
                  source={loadLandingPages}
                  lazy={true}
                  validate={validationLandingPage}
                  depends={DEPENDENCIES[LANDING_PAGE]}
                />
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <FormField.TextField
                multiline={true}
                minRows={1}
                padding={0}
                name={DESCRIPTION}
                label={'Description'}
              />
            </Grid>
          </Grid>
        </Section>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Section title={'Promo Code'}>
              <FormField.Select
                name={PROMO_CODE}
                placeholder={'Select promo code'}
                padding={0}
                source={loadPromoCodes}
                lazy={true}
                depends={DEPENDENCIES[PROMO_CODE]}
              />
            </Section>
          </Grid>
          <Grid item xs={4}>
            <Section title={'Cost Models *'}>
              <FormField.MultiSelect
                name={COST_MODEL}
                placeholder={'Select cost model'}
                padding={0}
                source={loadCustomModels}
                lazy={true}
                depends={DEPENDENCIES[PROMO_CODE]}
              />
            </Section>
          </Grid>
          <Grid item xs={2}>
            <Section title={'Budget *'}>
              <FormField.TextField
                type={'currency'}
                name={TOTAL_BUDGET}
                padding={0}
                placeholder={'$0'}
              />
            </Section>
          </Grid>
        </Grid>
        <Section title={'Payout Ratios'}>
          <Grid container spacing={3}>
            {selectedAndroidPlatform ? (
              <Grid item xs={2}>
                <FormField.TextField
                  type={'percent'}
                  name={`${PAYOUT_RATIOS}.${ANDROID}`}
                  label={'Android'}
                  padding={0}
                  placeholder={'0%'}
                  validate={validationPayoutAndroid}
                />
              </Grid>
            ) : null}
            {selectedIOSPlatform ? (
              <Grid item xs={2}>
                <FormField.TextField
                  type={'percent'}
                  name={`${PAYOUT_RATIOS}.${IOS}`}
                  label={'iOS'}
                  padding={0}
                  placeholder={'0%'}
                  validate={validationPayoutIOS}
                />
              </Grid>
            ) : null}
            {selectedPlariumPlayPlatform ? (
              <Grid item xs={2}>
                <FormField.TextField
                  type={'percent'}
                  name={`${PAYOUT_RATIOS}.${PLARIUM_PLAY}`}
                  label={'Plarium Play'}
                  padding={0}
                  placeholder={'0%'}
                  validate={validationPayoutPlariumPlay}
                />
              </Grid>
            ) : null}
            {selectedPortalPlatform ? (
              <Grid item xs={2}>
                <FormField.TextField
                  type={'percent'}
                  name={`${PAYOUT_RATIOS}.${PORTAL}`}
                  label={'Portal'}
                  padding={0}
                  placeholder={'0%'}
                  validate={validationPayoutPortal}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Alert severity={'info'}>
                Please note that the spend allocation for CFF, FCFF, and FCFF by
                clicks will be done automatically according to the number of
                Total Regs by platforms.
              </Alert>
            </Grid>
          </Grid>
        </Section>

        <Agencies
          attributionProviderId={initialValues[ATTRIBUTION_PROVIDER_ID]}
        />
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default MasterCampaignForm;
